import type { OmitDeep } from '@studio/js-utils';
import type { Identifier } from '@studio/models';
import type { Error as PbError } from '@tryiris-ai/common.protojs/api/v1/common';
import type { T_Maybe, T_Optional } from '@tryiris-ai/js-types';
import type { StatusCode } from 'status-code-enum';
import type { Opaque } from 'type-fest';
import { DefaultErrorStatus } from './constants.js';
import { StudioError } from '#~/error/index.js';

/** @internal */
export type ApiResponse = object & { $type: string; error: T_Maybe<OmitSyncProps<PbError>> };

/** Representation of ID in `base64` encoding. */
export type Base64Id = Opaque<Identifier, '__base64Id'>;

/** Representation of ID in byte encoding. */
export type ByteId = Uint8Array;

/** @internal */
export type OmitResponseProps<T extends ApiResponse> = Omit<T, '$type' | 'error'>;

/** Omits variant state synchronization properties. */
export type OmitSyncProps<T> = OmitDeep<T, '$type' | 'lastModified' | 'mutationId' | 'pending'>;

/** A custom `Error` class for responses from the API. */
export class ApiError extends StudioError {
  /** API error code (default `DefaultErrorStatus`) */
  public readonly code: StatusCode;

  /**
   * @param message - Error message.
   * @param options - Error options.
   */
  public constructor(
    message?: T_Maybe<string>,
    options?: T_Maybe<T_Optional<ErrorOptions & { code: StatusCode }>>
  ) {
    const { code = DefaultErrorStatus, ...rest } = options ?? {};

    super(message ?? `[API-${code}] - An API error has occurred.`, rest);

    this.name = 'ApiError';
    this.code = code;
  }
}
